import { z } from 'zod';
import { assistantActivitySchema } from './assistant.js';
/**
 * Conversa ao vivo com o Secretário (tocar e segurar o microfone do rodapé).
 * O aparelho fala direto com o provedor de voz usando uma chave temporária
 * que a API emite já travada (modelo, instruções, ferramentas e voz); a API
 * executa as ferramentas, conta os minutos e guarda só o texto da conversa.
 */
/** Versão do texto de consentimento da conversa ao vivo (preferences.voiceConsent). */
export const VOICE_CONSENT_VERSION = 1;
/** Silêncio que o ditado do toque simples aceita antes de encerrar. */
export const DICTATION_SILENCE_MS = 8000;
/** Vozes oferecidas, por gênero do personagem escolhido. A primeira de cada lista é a padrão. */
export const VOICE_CATALOG = {
    ela: [
        { id: 'Sulafat', label: 'Calorosa' },
        { id: 'Achernar', label: 'Delicada' },
        { id: 'Aoede', label: 'Leve' },
        { id: 'Kore', label: 'Firme' },
    ],
    ele: [
        { id: 'Orus', label: 'Firme' },
        { id: 'Achird', label: 'Amigável' },
        { id: 'Sadaltager', label: 'Experiente' },
        { id: 'Charon', label: 'Nítida' },
    ],
    // Gota, Robô S e as Faces não têm gênero: quem escolhe o timbre é a pessoa.
    // As duas primeiras são as de sempre (apps antigos só conhecem essas).
    neutro: [
        { id: 'Orus', label: 'Firme' },
        { id: 'Achernar', label: 'Delicada' },
        { id: 'Sulafat', label: 'Calorosa' },
        { id: 'Achird', label: 'Amigável' },
        { id: 'Aoede', label: 'Leve' },
        { id: 'Sadaltager', label: 'Experiente' },
    ],
};
export const VOICE_IDS = [
    ...new Set(Object.values(VOICE_CATALOG).flatMap((list) => list.map((voice) => voice.id))),
];
export const voiceIdSchema = z.enum(VOICE_IDS);
/** Personagens com nome próprio na chave (sem `-ela`/`-ele`) e o gênero deles. */
const NAMED_GENDER = { 'traco-igor': 'ele' };
/** Gênero do personagem pela chave: `-ela`, `-ele` ou neutro (símbolos, avatar próprio, bichos). */
export function voiceGenderOf(characterKey) {
    const named = NAMED_GENDER[characterKey];
    if (named)
        return named;
    if (/-ela\d*$/.test(characterKey))
        return 'ela';
    if (characterKey.endsWith('-ele'))
        return 'ele';
    return 'neutro';
}
/** A voz que vale para o personagem: a escolhida, se combina com o gênero dele; senão a padrão. */
export function voiceFor(characterKey, chosen) {
    const options = VOICE_CATALOG[voiceGenderOf(characterKey)];
    const match = options.find((voice) => voice.id === chosen);
    return (match ?? options[0]).id;
}
export const voiceModeSchema = z.enum(['fast', 'deep']);
const voiceLineSchema = z.object({
    role: z.enum(['owner', 'assistant']),
    text: z.string().trim().min(1).max(2000),
});
/** Pedido de chave: nova conversa, troca para o modo que pensa com calma ou retomada. */
export const voiceSessionRequestSchema = z.object({
    mode: voiceModeSchema.default('fast'),
    /** Conversa em andamento (troca de modo ou renovação da chave). */
    sessionId: z.uuid().optional(),
    /** Últimas falas, para o novo modo saber do que se falava. */
    history: z.array(voiceLineSchema).max(12).default([]),
    /** Este app sabe abrir e fechar painéis a pedido do Secretário (palco). */
    screen: z.boolean().optional(),
    /** Este app também abre páginas inteiras e os painéis novos (Caderno, cartela). */
    pages: z.boolean().optional(),
    /** Este app mostra o pedido de documento para o dono confirmar na tela. */
    docs: z.boolean().optional(),
    /** Este app tem a página Objetivos: libera as ferramentas de objetivos e a página na voz. */
    goals: z.boolean().optional(),
    /** O app desenha o visual vivo: libera a ferramenta do visual (barba). */
    look: z.boolean().optional(),
    recipes: z.boolean().optional(),
    /** O app tem os Documentos novos (busca, fixados, lixeira): libera a procura por voz. */
    hd: z.boolean().optional(),
    /** O app desenha o cartão de saída (rota, corrida, ligação): libera as ferramentas de link. */
    links: z.boolean().optional(),
    /** O aparelho lê avisos de outros apps (Android, permissão ligada): libera ver e responder pela voz. */
    inbox: z.boolean().optional(),
    /** O app tem a página Idiomas: libera abrir a aula e contar o progresso pela voz. */
    idiomas: z.boolean().optional(),
    /** O app tem a Clínica (Meu negócio › Clínica): libera as ferramentas da clínica pela voz. */
    clinic: z.boolean().optional(),
});
export const voiceSessionGrantSchema = z.object({
    sessionId: z.uuid(),
    mode: voiceModeSchema,
    /** Chave temporária de uso único (o aparelho nunca vê a chave do servidor). */
    token: z.string().min(1),
    model: z.string().min(1),
    url: z.url(),
    voice: z.string().min(1),
    /** Até quando a chave aceita áudio; depois disso o provedor encerra. */
    expiresAt: z.iso.datetime(),
    secondsLeft: z.number().int().nonnegative(),
});
export const voiceToolCallSchema = z.object({
    callId: z.string().trim().min(1).max(200),
    name: z.string().trim().min(1).max(80),
    args: z.record(z.string(), z.unknown()).default({}),
    /** A última fala da pessoa (o transcrito do aparelho): o sim de uma ação vem daqui (F0). */
    heard: z.string().trim().max(200).optional(),
});
/** Resposta de uma ferramenta: os dados para o modelo e a atividade para a tela. */
export const voiceToolResultSchema = z.object({
    response: z.record(z.string(), z.unknown()),
    activity: assistantActivitySchema,
});
export const voiceHeartbeatSchema = z.object({
    /** Falas novas desde a última batida, em texto (o áudio nunca vem para cá). */
    lines: z.array(voiceLineSchema).max(40).default([]),
    /** Contagem de uso informada pelo provedor (só números). */
    usage: z.record(z.string(), z.unknown()).optional(),
});
export const voiceEndSchema = voiceHeartbeatSchema.extend({
    reason: z.enum(['owner', 'goodbye', 'quota', 'error', 'background', 'replaced']).default('owner'),
});
export const voiceBeatResultSchema = z.object({
    secondsLeft: z.number().int().nonnegative(),
});
